import React from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  EditButton,
  DeleteButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  ReferenceField,
  FunctionField,
  useTranslate,
  useDataProvider,
  useNotify,
  useListContext,
  BulkDeleteButton,
  useRefresh,
  useGetList,
  useUnselect,
} from 'react-admin'
import {
  DocumentIcon,
  ArrowPathIcon,
  InboxIcon,
} from '@heroicons/react/24/outline'
import {
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  LinearProgress,
  TablePagination,
  Checkbox,
} from '@mui/material'

type VendorSummaryRow = {
  vendor_id: number
  vendor_name: string
  total_contracts: number
  summary_status: string
  completed_count: number
  review_required_count: number
}

type DocumentTypeSummaryRow = {
  extracted_document_type: string
  total_contracts: number
  summary_status: string
  completed_count: number
  review_required_count: number
}

type StaleContractRow = {
  contract_id: number
  filename: string
  status: string
  is_stale: boolean
  reasons: string[]
}

const ContractEmpty = ({ tab }) => {
  const { filterValues } = useListContext()
  const isVerifiedTab = tab === 'verified'
  const isReviewTab = tab === 'review'
  const isProcessingTab = tab === 'processing'
  const isFailedTab = tab === 'failed'
  const isNeedsLinkingTab = tab === 'needs_linking'
  const isStaleTab = tab === 'stale'
  const isMissingSourceTab = tab === 'missing_source'

  if (isVerifiedTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Verified Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Check the &quot;Needs Review&quot; tab to verify and approve newly
          uploaded documents.
        </Typography>
      </Box>
    )
  }

  if (isReviewTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Contracts Need Review
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          All contracts have been processed or are already verified.
        </Typography>
      </Box>
    )
  }

  if (isFailedTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Failed Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Contracts that fail extraction will appear here for reprocessing.
        </Typography>
      </Box>
    )
  }

  if (isProcessingTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Contracts Processing
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Queued and actively processing contracts will appear here.
        </Typography>
      </Box>
    )
  }

  if (isNeedsLinkingTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Contracts Need Linking
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Contracts with a parent reference but no linked parent will appear
          here.
        </Typography>
      </Box>
    )
  }

  if (isStaleTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Stale Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Contracts that need reprocessing due to pipeline/prompt version
          changes will appear here.
        </Typography>
      </Box>
    )
  }

  if (isMissingSourceTab) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
        <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          No Missing Source Contracts
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Contracts whose source files are missing from storage will appear
          here.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ textAlign: 'center', mt: 8, p: 4 }}>
      <InboxIcon className="h-16 w-16 mx-auto text-stone-300" />
      <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
        No Contract Repository yet.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
        Do you want to add one?
      </Typography>
      <CreateButton label="Create" variant="contained" />
    </Box>
  )
}

const ContractStatusChip = ({ record }) => {
  if (!record) return null

  const colors = {
    uploaded: 'default',
    processing: 'primary',
    review_required: 'warning',
    completed: 'success',
    failed: 'error',
    extraction_failed: 'error',
    missing_source_file: 'error',
  }

  const labelMap = {
    uploaded: 'UPLOADED',
    processing: 'PROCESSING',
    review_required: 'REVIEW REQUIRED',
    completed: 'VERIFIED',
    failed: 'FAILED',
    extraction_failed: 'EXTRACTION FAILED',
    missing_source_file: 'MISSING SOURCE FILE',
  }
  const label = labelMap[record.status] || 'UNKNOWN'
  const hasProgress =
    record.status === 'processing' &&
    (record.extraction_stage_label ||
      record.extraction_status_message ||
      typeof record.extraction_stage_percent === 'number')
  const normalizedPercent =
    typeof record.extraction_stage_percent === 'number'
      ? Math.max(0, Math.min(100, record.extraction_stage_percent))
      : record.extraction_stage_index && record.extraction_stage_total
      ? Math.max(
          0,
          Math.min(
            100,
            (record.extraction_stage_index / record.extraction_stage_total) *
              100
          )
        )
      : null

  const chip = (
    <Chip
      label={label}
      color={colors[record.status] || 'default'}
      size="small"
      variant="filled"
    />
  )

  if (!hasProgress) return chip

  return (
    <Box sx={{ minWidth: 220 }}>
      <Box sx={{ mb: 0.75 }}>{chip}</Box>
      <LinearProgress
        variant={normalizedPercent === null ? 'indeterminate' : 'determinate'}
        value={normalizedPercent === null ? undefined : normalizedPercent}
        sx={{ height: 6, borderRadius: 3 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.5, lineHeight: 1.2 }}
      >
        {record.extraction_stage_label || 'Processing'}
        {normalizedPercent !== null
          ? ` (${Math.round(normalizedPercent)}%)`
          : ''}
      </Typography>
    </Box>
  )
}

function getPersistedMetadataCount(record) {
  if (!Array.isArray(record?.metadata_fields)) return 0
  return record.metadata_fields.filter((row) => Number(row?.id) > 0).length
}

function isTerminalNoDataFailure(record) {
  return (
    typeof record?.extraction_error_message === 'string' &&
    (record.extraction_error_message.includes(
      'No usable metadata values extracted from document'
    ) ||
      record.extraction_error_message.includes(
        'Unsupported PDF portfolio format'
      ))
  )
}

function isRetryEligible(record) {
  if (!record) return false
  const persistedMetadataCount = getPersistedMetadataCount(record)
  const terminalNoDataFailure = isTerminalNoDataFailure(record)
  return (
    record.status === 'uploaded' ||
    record.status === 'processing' ||
    record.status === 'extraction_failed' ||
    (record.status === 'failed' && !terminalNoDataFailure) ||
    (record.status === 'review_required' && persistedMetadataCount === 0)
  )
}

function getReprocessingReason(record) {
  if (!record) return ''
  const persistedMetadataCount = getPersistedMetadataCount(record)

  if (record.status === 'uploaded') {
    return (
      record.extraction_status_message ||
      'Uploaded but extraction has not completed yet'
    )
  }
  if (record.status === 'processing') {
    return (
      record.extraction_status_message ||
      'Extraction is currently running; wait or retry if it stays stuck'
    )
  }
  if (record.status === 'failed' || record.status === 'extraction_failed') {
    return (
      record.extraction_error_message ||
      record.extraction_status_message ||
      'Extraction failed'
    )
  }
  if (record.status === 'missing_source_file') {
    return 'Source file missing in storage; re-upload or purge this contract'
  }
  if (record.status === 'review_required' && persistedMetadataCount === 0) {
    return (
      record.extraction_error_message ||
      'No usable metadata was extracted from this document'
    )
  }
  return 'Requires reprocessing'
}

const RetryButton = ({ record }) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = React.useState(false)
  const canRetry = isRetryEligible(record)

  if (!canRetry) return null

  const handleRetry = (e) => {
    e.stopPropagation() // Prevent row click
    setLoading(true)
    const forceRetry = record.status === 'processing'
    dataProvider
      .retryExtraction(record.id, forceRetry)
      .then(() => {
        notify('Extraction retried successfully', { type: 'info' })
        refresh()
      })
      .catch((error) => {
        notify(`Error retrying extraction: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Tooltip
      title={
        record.status === 'uploaded'
          ? 'Start extraction'
          : record.status === 'processing'
          ? 'Force retry extraction'
          : record.status === 'review_required'
          ? 'Retry extraction (no fields extracted)'
          : 'Retry AI Extraction'
      }
    >
      <IconButton
        onClick={handleRetry}
        disabled={loading}
        size="small"
        sx={{
          color:
            record.status === 'review_required' || record.status === 'uploaded'
              ? '#2563eb'
              : '#ef4444',
        }}
      >
        <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
      </IconButton>
    </Tooltip>
  )
}

const UNIVERSAL_FIELDS = [
  'Supplier Name',
  'Signatory Details',
  'Contract Type',
  'Categorisation',
  'Linked Contracts',
  'Parent Agreement',
  'Referenced Schedules',
  'Effective Date',
  'Expiration Date',
  'Auto-Renewal Clause',
  'Notice Period',
  'Payment Terms',
  'Contract Value',
  'Liability Cap',
  'Indemnification',
  'Governing Law',
  'Dispute Resolution',
  'Termination Rights',
  'Change of Control',
  'Data Privacy',
  'Confidentiality',
  'Force Majeure',
  'Insurance',
  'Assignment Rights',
]

const isFieldMandatory = (fieldName) => {
  if (!fieldName) return false
  const normalizedFieldName = fieldName.toLowerCase()
  return UNIVERSAL_FIELDS.some(
    (uf) =>
      normalizedFieldName.includes(uf.toLowerCase()) ||
      uf.toLowerCase().includes(normalizedFieldName)
  )
}

const ConfidenceIndicator = ({ record }) => {
  if (!record) return null

  if (typeof record.ai_confidence === 'number') {
    const confidence = Math.round(record.ai_confidence * 100)
    let color = 'bg-red-500'
    if (confidence >= 80) color = 'bg-green-500'
    else if (confidence >= 50) color = 'bg-amber-500'

    return (
      <div className="flex items-center space-x-2">
        <div className={`w-3 h-3 rounded-full ${color}`} />
        <span className="text-xs text-stone-500">{confidence}%</span>
      </div>
    )
  }

  if (!record.metadata_fields || record.metadata_fields.length === 0)
    return null

  // Calculate average confidence for fields.
  // We include mandatory fields in the average even if they are empty (treated as 0%).
  // Optional fields that were not found (value is null, empty or 'None') are excluded.
  const fieldsForConfidence = record.metadata_fields.filter((f) => {
    const isMandatory = isFieldMandatory(f.field_name)
    const hasValue =
      f.field_value &&
      f.field_value !== 'None' &&
      f.field_value !== 'null' &&
      f.field_value.trim() !== ''
    return isMandatory || hasValue
  })

  if (fieldsForConfidence.length === 0) return null

  const sumScores = fieldsForConfidence.reduce((a, b) => {
    const hasValue =
      b.field_value &&
      b.field_value !== 'None' &&
      b.field_value !== 'null' &&
      b.field_value.trim() !== ''
    return a + (hasValue ? b.confidence_score || 0 : 0)
  }, 0)

  const avgScore = sumScores / fieldsForConfidence.length
  const confidence = Math.round(avgScore * 100)

  let color = 'bg-red-500'
  if (confidence >= 80) color = 'bg-green-500'
  else if (confidence >= 50) color = 'bg-amber-500'

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-3 h-3 rounded-full ${color}`} />
      <span className="text-xs text-stone-500">{confidence}%</span>
    </div>
  )
}

const ContractListActions = ({ tab }) => {
  const { selectedIds, data } = useListContext()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselect = useUnselect('contracts')
  const [retryLoading, setRetryLoading] = React.useState(false)
  const [reprocessAllLoading, setReprocessAllLoading] = React.useState(false)
  const [reprocessStaleLoading, setReprocessStaleLoading] =
    React.useState(false)
  const [purgeMissingLoading, setPurgeMissingLoading] = React.useState(false)
  const selectionTrue = selectedIds && selectedIds.length > 0

  const confirmContent = `Are you sure you want to delete ${selectedIds?.length} selected contracts? This action cannot be undone.`
  const dataRows = Object.values(data || {})
  const selectedIdSet = new Set((selectedIds || []).map((id) => String(id)))
  const selectedRecords = dataRows.filter((row) =>
    selectedIdSet.has(String(row?.id))
  )

  // For "select all" flows, selectedIds may include IDs not loaded in current page.
  // Keep those IDs retry-eligible by default and let API responses determine outcomes.
  const knownRecordIds = new Set(selectedRecords.map((row) => String(row.id)))
  const unknownSelectedIds = (selectedIds || []).filter(
    (id) => !knownRecordIds.has(String(id))
  )

  const retryableKnownRecords = selectedRecords.filter(isRetryEligible)
  const retryableSelectedCount =
    retryableKnownRecords.length + unknownSelectedIds.length

  const handleRetrySelected = () => {
    if (!selectedIds?.length) return
    const selectedIdsSnapshot = [...selectedIds]
    const retryIds = [
      ...retryableKnownRecords.map((record) => record.id),
      ...unknownSelectedIds,
    ]
    if (!retryIds.length) return
    setRetryLoading(true)
    unselect(selectedIdsSnapshot)
    Promise.allSettled(retryIds.map((id) => dataProvider.retryExtraction(id)))
      .then((results) => {
        const successCount = results.filter(
          (r) => r.status === 'fulfilled'
        ).length
        const failedCount = results.length - successCount
        if (failedCount > 0) {
          notify(
            `Queued ${successCount} retries. ${failedCount} failed to queue.`,
            { type: successCount > 0 ? 'warning' : 'error' }
          )
        } else {
          notify(`Queued ${successCount} retries`, { type: 'info' })
        }
        refresh()
      })
      .finally(() => {
        setRetryLoading(false)
      })
  }

  const handleReprocessAllContracts = () => {
    const confirmed = window.confirm(
      'Queue reprocessing for all contracts in this organisation? Existing extracted metadata will be replaced. Any stuck in-progress tasks will be superseded.'
    )
    if (!confirmed) return

    setReprocessAllLoading(true)
    dataProvider
      .reprocessAllContracts({ limit: 2000, force: true })
      .then((result) => {
        notify(
          `Queued contract reprocessing: ${
            result?.queued_now_estimate ?? 0
          } now, in-flight ${result?.inflight_count ?? 0}/${
            result?.inflight_cap ?? 0
          }, remaining ${result?.remaining_estimate ?? 0}.`,
          { type: 'info' }
        )
        refresh()
      })
      .catch((error) => {
        notify(`Error reprocessing all contracts: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setReprocessAllLoading(false)
      })
  }

  const handleReprocessStale = () => {
    setReprocessStaleLoading(true)
    dataProvider
      .reprocessStaleContracts({ limit: 500 })
      .then((result) => {
        if (result?.status === 'queued') {
          notify('Stale reprocessing queued in background', { type: 'info' })
          refresh()
          return
        }
        const queued = result?.queued_count || 0
        const skipped = result?.skipped_count || 0
        if (queued > 0) {
          notify(`Queued ${queued} stale contracts for reprocessing`, {
            type: skipped > 0 ? 'warning' : 'info',
          })
        } else {
          notify('No stale contracts were queued', { type: 'warning' })
        }
        refresh()
      })
      .catch((error) => {
        notify(`Error reprocessing stale contracts: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setReprocessStaleLoading(false)
      })
  }

  const handlePurgeMissingSource = () => {
    const confirmed = window.confirm(
      'Purge all contracts with missing source files? This permanently deletes these contract records.'
    )
    if (!confirmed) return
    setPurgeMissingLoading(true)
    dataProvider
      .reconcileMissingSourceContracts({ limit: 5000, archive_missing: true })
      .then((result) => {
        notify(
          `Purged ${result?.archived_count ?? 0} missing-source contracts`,
          { type: 'info' }
        )
        refresh()
      })
      .catch((error) => {
        notify(`Error purging missing-source contracts: ${error.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setPurgeMissingLoading(false)
      })
  }

  const toolbarButtonSx = {
    marginRight: 2,
    minHeight: 48,
    px: 2.5,
    borderRadius: 1.5,
    textTransform: 'none',
    fontWeight: 600,
  }

  return (
    <TopToolbar
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'transparent !important',
      }}
    >
      <CreateButton
        label="Upload Contract"
        variant="contained"
        sx={toolbarButtonSx}
      />
      <BulkDeleteButton
        label="Delete Selected"
        disabled={!selectionTrue}
        mutationMode="pessimistic"
        confirmTitle="Confirm Bulk Deletion"
        confirmContent={confirmContent}
        sx={{
          ...toolbarButtonSx,
          color: selectionTrue ? 'white' : 'inherit',
          backgroundColor: selectionTrue ? '#ef4444' : 'transparent',
          '&:hover': {
            backgroundColor: selectionTrue ? '#dc2626' : 'transparent',
          },
          '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
        }}
      />
      {tab === 'failed' && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetrySelected}
          disabled={
            retryLoading || !selectedIds?.length || retryableSelectedCount === 0
          }
          sx={toolbarButtonSx}
        >
          {retryLoading
            ? 'Queuing...'
            : `Retry Selected (${retryableSelectedCount})`}
        </Button>
      )}
      {tab === 'all' && (
        <Button
          variant="outlined"
          onClick={handleReprocessAllContracts}
          disabled={reprocessAllLoading}
          sx={toolbarButtonSx}
        >
          {reprocessAllLoading
            ? 'Queuing Reprocess...'
            : 'Reprocess All Contracts'}
        </Button>
      )}
      {tab === 'stale' && (
        <Button
          variant="outlined"
          onClick={handleReprocessStale}
          disabled={reprocessStaleLoading}
          sx={toolbarButtonSx}
        >
          {reprocessStaleLoading ? 'Queuing Stale...' : 'Reprocess Stale'}
        </Button>
      )}
      {tab === 'missing_source' && (
        <Button
          variant="contained"
          color="error"
          onClick={handlePurgeMissingSource}
          disabled={purgeMissingLoading}
          sx={toolbarButtonSx}
        >
          {purgeMissingLoading ? 'Purging...' : 'Purge Missing Source'}
        </Button>
      )}
      <ExportButton sx={toolbarButtonSx} />
    </TopToolbar>
  )
}

const isProcessing = (status) => ['processing'].includes(status)

const PollingComponent = ({ tab }: { tab: string }) => {
  const { refetch, isFetching, data } = useListContext()
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const isFetchingRef = React.useRef(isFetching)
  const rows = React.useMemo(() => Object.values(data || {}), [data])
  const hasInFlightRowsOnPage = React.useMemo(
    () =>
      rows.some((row) =>
        ['uploaded', 'processing'].includes(String(row?.status || ''))
      ),
    [rows]
  )

  // Keep isFetchingRef in sync
  React.useEffect(() => {
    isFetchingRef.current = isFetching
  }, [isFetching])

  const shouldPoll =
    tab === 'processing' ||
    tab === 'all' ||
    tab === 'review' ||
    tab === 'failed' ||
    hasInFlightRowsOnPage

  React.useEffect(() => {
    if (!shouldPoll) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return undefined
    }

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    timerRef.current = setInterval(() => {
      if (!isFetchingRef.current) {
        refetch()
      }
    }, 5000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [refetch, shouldPoll])

  // One final refresh when in-flight rows disappear from the current page.
  const prevHasInFlightRows = React.useRef(false)
  React.useEffect(() => {
    if (prevHasInFlightRows.current && !hasInFlightRowsOnPage) {
      refetch()
    }
    prevHasInFlightRows.current = hasInFlightRowsOnPage
  }, [hasInFlightRowsOnPage, refetch])

  return null
}

const VendorContractsTable = ({
  onVendorClick,
}: {
  onVendorClick: (vendor: VendorSummaryRow) => void
}) => {
  const { data, isLoading, error } = useGetList('contracts/by-vendor', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'total_contracts', order: 'DESC' },
    filter: {},
  })
  const rows = (data || []) as VendorSummaryRow[]

  const statusChip = (status) => {
    if (status === 'verified')
      return <Chip size="small" label="Verified" color="success" />
    if (status === 'review_required')
      return <Chip size="small" label="Needs Review" color="warning" />
    return <Chip size="small" label="In Progress" color="default" />
  }

  if (isLoading) {
    return <Typography sx={{ p: 2 }}>Loading vendors...</Typography>
  }

  if (error) {
    return (
      <Typography sx={{ p: 2 }} color="error">
        Could not load vendors.
      </Typography>
    )
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Contracts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.vendor_id} hover>
              <TableCell>
                <Button
                  variant="text"
                  onClick={() => onVendorClick(row)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {row.vendor_name}
                </Button>
              </TableCell>
              <TableCell>{statusChip(row.summary_status)}</TableCell>
              <TableCell align="right">{row.total_contracts}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">
                  No vendors with contracts found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

const DocumentTypeContractsTable = ({
  onTypeClick,
}: {
  onTypeClick: (docType: string) => void
}) => {
  const { data, isLoading, error } = useGetList('contracts/by-document-type', {
    pagination: { page: 1, perPage: 200 },
    sort: { field: 'total_contracts', order: 'DESC' },
    filter: {},
  })
  const rows = (data || []) as DocumentTypeSummaryRow[]

  const statusChip = (status) => {
    if (status === 'verified')
      return <Chip size="small" label="Verified" color="success" />
    if (status === 'review_required')
      return <Chip size="small" label="Needs Review" color="warning" />
    return <Chip size="small" label="In Progress" color="default" />
  }

  if (isLoading) {
    return <Typography sx={{ p: 2 }}>Loading document types...</Typography>
  }

  if (error) {
    return (
      <Typography sx={{ p: 2 }} color="error">
        Could not load document types.
      </Typography>
    )
  }

  return (
    <Paper variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Document Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Contracts</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.extracted_document_type} hover>
              <TableCell>
                <Button
                  variant="text"
                  onClick={() => onTypeClick(row.extracted_document_type)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {row.extracted_document_type}
                </Button>
              </TableCell>
              <TableCell>{statusChip(row.summary_status)}</TableCell>
              <TableCell align="right">{row.total_contracts}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography color="text.secondary">
                  No extracted document types found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

const formatStaleReason = (reason: string) => {
  if (!reason) return ''
  return reason
    .replace(/_/g, ' ')
    .replace(/:/g, ' - ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

const StaleContractsTable = () => {
  const [page, setPage] = React.useState(0)
  const [perPage, setPerPage] = React.useState(25)
  const [queueSnapshot, setQueueSnapshot] = React.useState<{
    queuedNow: number
    inflight: number
    cap: number
    remaining: number
  } | null>(null)
  const [showBacklogList, setShowBacklogList] = React.useState(false)
  const { data, total, isLoading, error, refetch } = useGetList(
    'contracts/stale',
    {
      pagination: { page: page + 1, perPage },
      sort: { field: 'contract_id', order: 'DESC' },
      filter: {},
    },
    {
      staleTime: 0,
      refetchInterval: 5000,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      keepPreviousData: true,
    }
  )
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const liveCountQueryOptions = React.useMemo(
    () => ({
      refetchInterval: 5000,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),
    []
  )
  const { total: processingTotal = 0 } = useGetList(
    'contracts',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { status: 'processing' },
    },
    liveCountQueryOptions
  )
  const [reprocessingIds, setReprocessingIds] = React.useState<Set<number>>(
    new Set()
  )
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const rows = (data || []) as StaleContractRow[]
  const totalRows = total || 0
  const queueBacklogFromSnapshot =
    (queueSnapshot?.queuedNow || 0) > 0 ||
    (queueSnapshot?.inflight || 0) > 0 ||
    (queueSnapshot?.remaining || 0) > 0
  const queueBacklogFromServer = processingTotal > 0
  const queueBacklogActive = queueBacklogFromSnapshot || queueBacklogFromServer
  const queueActionsLocked = queueBacklogFromSnapshot

  const handleReprocessOne = (contractId: number) => {
    setReprocessingIds((prev) => new Set(prev).add(contractId))
    dataProvider
      .reprocessStaleContracts({ contract_ids: [contractId], limit: 1 })
      .then((result) => {
        if (result?.status === 'queued') {
          setQueueSnapshot({
            queuedNow: result?.queued_now_estimate ?? 0,
            inflight: result?.inflight_count ?? 0,
            cap: result?.inflight_cap ?? 0,
            remaining: result?.remaining_estimate ?? 0,
          })
        }
        notify(`Queued reprocessing for contract ${contractId}`, {
          type: 'info',
        })
        refetch()
        refresh()
      })
      .catch((err) => {
        notify(`Failed to queue contract ${contractId}: ${err.message}`, {
          type: 'warning',
        })
      })
      .finally(() => {
        setReprocessingIds((prev) => {
          const next = new Set(prev)
          next.delete(contractId)
          return next
        })
      })
  }

  const allVisibleIds = rows.map((row) => row.contract_id)
  const allVisibleSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id))

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        allVisibleIds.forEach((id) => next.delete(id))
      } else {
        allVisibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const toggleSelected = (contractId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(contractId)) {
        next.delete(contractId)
      } else {
        next.add(contractId)
      }
      return next
    })
  }

  const handleReprocessSelected = () => {
    const ids = Array.from(selectedIds)
    if (!ids.length) return
    dataProvider
      .reprocessStaleContracts({ contract_ids: ids, limit: ids.length })
      .then((result) => {
        if (result?.status === 'queued') {
          setQueueSnapshot({
            queuedNow: result?.queued_now_estimate ?? 0,
            inflight: result?.inflight_count ?? 0,
            cap: result?.inflight_cap ?? 0,
            remaining: result?.remaining_estimate ?? 0,
          })
          setShowBacklogList(false)
          notify(
            `Queued stale batch: now ${
              result?.queued_now_estimate ?? 0
            }, in-flight ${result?.inflight_count ?? 0}/${
              result?.inflight_cap ?? 0
            }, remaining ${result?.remaining_estimate ?? 0}.`,
            { type: 'info' }
          )
          setSelectedIds(new Set())
          refetch()
          refresh()
          return
        }
        notify(`Queued ${result?.queued_count || 0} selected stale contracts`, {
          type: 'info',
        })
        setSelectedIds(new Set())
        refetch()
        refresh()
      })
      .catch((err) => {
        notify(`Failed to queue selected stale contracts: ${err.message}`, {
          type: 'warning',
        })
      })
  }

  const handleReprocessAll = () => {
    dataProvider
      .reprocessStaleContracts({ limit: 2000 })
      .then((result) => {
        if (result?.status === 'queued') {
          setQueueSnapshot({
            queuedNow: result?.queued_now_estimate ?? 0,
            inflight: result?.inflight_count ?? 0,
            cap: result?.inflight_cap ?? 0,
            remaining: result?.remaining_estimate ?? 0,
          })
          setShowBacklogList(false)
          notify(
            `Queued stale batch: now ${
              result?.queued_now_estimate ?? 0
            }, in-flight ${result?.inflight_count ?? 0}/${
              result?.inflight_cap ?? 0
            }, remaining ${result?.remaining_estimate ?? 0}.`,
            { type: 'info' }
          )
          setSelectedIds(new Set())
          refetch()
          refresh()
          return
        }
        notify(`Queued ${result?.queued_count || 0} stale contracts`, {
          type: 'info',
        })
        setSelectedIds(new Set())
        refetch()
        refresh()
      })
      .catch((err) => {
        notify(`Failed to queue stale contracts: ${err.message}`, {
          type: 'warning',
        })
      })
  }

  const handleReconcileQueue = () => {
    dataProvider
      // Minimal request to trigger backend stale-queue reconciliation logic.
      .reprocessStaleContracts({ limit: 1 })
      .then((result) => {
        notify(
          `Queue reconciliation triggered. Reconciled ${
            result?.reconciled_orphan_count ?? 0
          } orphan tasks.`,
          { type: 'info' }
        )
        refetch()
        refresh()
      })
      .catch((err) => {
        notify(`Failed to reconcile queue: ${err.message}`, {
          type: 'warning',
        })
      })
  }

  if (isLoading) {
    return <Typography sx={{ p: 2 }}>Loading stale contracts...</Typography>
  }

  if (error) {
    return (
      <Typography sx={{ p: 2 }} color="error">
        Could not load stale contracts.
      </Typography>
    )
  }

  if (!rows.length) {
    return <ContractEmpty tab="stale" />
  }

  return (
    <Paper variant="outlined">
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {totalRows} stale contracts
        </Typography>
        {!queueActionsLocked ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={toggleSelectAllVisible}
            >
              {allVisibleSelected
                ? 'Clear Page Selection'
                : 'Select All (Page)'}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleReprocessSelected}
              disabled={selectedIds.size === 0}
            >
              Reprocess Selected ({selectedIds.size})
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleReprocessAll}
            >
              Reprocess All
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Reprocess queue is active.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handleReconcileQueue}
            >
              Reconcile Queue
            </Button>
          </Box>
        )}
      </Box>
      {queueBacklogActive && !showBacklogList && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Reprocess queue active:{' '}
            {queueSnapshot
              ? `${queueSnapshot?.queuedNow || 0} queued now, ${
                  queueSnapshot?.inflight || 0
                }/${queueSnapshot?.cap || 0} in flight, ${
                  queueSnapshot?.remaining || 0
                } waiting for next batches.`
              : `${processingTotal} currently processing.`}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowBacklogList(true)}
          >
            Show backlog list
          </Button>
        </Box>
      )}
      {queueBacklogActive && showBacklogList && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setShowBacklogList(false)}
          >
            Hide backlog list
          </Button>
        </Box>
      )}
      {(!queueBacklogActive || showBacklogList) && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={
                      selectedIds.size > 0 &&
                      !allVisibleSelected &&
                      allVisibleIds.length > 0
                    }
                    onChange={toggleSelectAllVisible}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stale Reasons</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.contract_id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(row.contract_id)}
                      onChange={() => toggleSelected(row.contract_id)}
                    />
                  </TableCell>
                  <TableCell>{row.contract_id}</TableCell>
                  <TableCell>{row.filename}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={(row.status || '').toUpperCase()}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {(row.reasons || []).map((reason) => (
                        <Chip
                          key={`${row.contract_id}-${reason}`}
                          size="small"
                          label={formatStaleReason(reason)}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        href={`#/contracts/${row.contract_id}`}
                      >
                        Open
                      </Button>
                      {!queueActionsLocked ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleReprocessOne(row.contract_id)}
                          disabled={reprocessingIds.has(row.contract_id)}
                        >
                          {reprocessingIds.has(row.contract_id)
                            ? 'Queuing...'
                            : 'Reprocess'}
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" disabled>
                          Queued
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(event) => {
              const next = parseInt(event.target.value, 10)
              setPerPage(next)
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </>
      )}
    </Paper>
  )
}

export const ContractList = () => {
  const [tab, setTab] = React.useState('all')
  const [selectedVendor, setSelectedVendor] =
    React.useState<VendorSummaryRow | null>(null)
  const [selectedDocumentType, setSelectedDocumentType] = React.useState<
    string | null
  >(null)
  const liveCountQueryOptions = React.useMemo(
    () => ({
      refetchInterval: 5000,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }),
    []
  )
  const { total: processingTotal = 0 } = useGetList(
    'contracts',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { status: 'processing' },
    },
    liveCountQueryOptions
  )
  const { total: failedTotal = 0 } = useGetList(
    'contracts',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: {
        needs_reprocessing: true,
        status: ['failed', 'extraction_failed', 'review_required'],
      },
    },
    liveCountQueryOptions
  )
  const { total: needsLinkingTotal = 0 } = useGetList(
    'contracts',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { needs_linking: true },
    },
    liveCountQueryOptions
  )
  const { total: missingSourceTotal = 0 } = useGetList(
    'contracts',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'DESC' },
      filter: { status: 'missing_source_file' },
    },
    liveCountQueryOptions
  )

  const handleTabChange = (event, newValue) => {
    setTab(newValue)
    if (newValue === 'by_vendor') {
      setSelectedVendor(null)
    }
    if (newValue === 'by_document_type') {
      setSelectedDocumentType(null)
    }
  }

  const baseFilter =
    tab === 'all'
      ? {}
      : tab === 'verified'
      ? { is_verified: true }
      : tab === 'processing'
      ? { status: 'processing' }
      : tab === 'failed'
      ? {
          needs_reprocessing: true,
          status: ['failed', 'extraction_failed', 'review_required'],
        }
      : tab === 'needs_linking'
      ? { needs_linking: true }
      : tab === 'stale'
      ? {}
      : tab === 'missing_source'
      ? { status: 'missing_source_file' }
      : { status: 'review_required' }
  const filter = selectedVendor?.vendor_id
    ? { ...baseFilter, vendor_id: selectedVendor.vendor_id }
    : selectedDocumentType
    ? { ...baseFilter, extracted_document_type: selectedDocumentType }
    : baseFilter

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label="All Contracts" value="all" />
          <Tab label="Needs Review" value="review" />
          <Tab label="Verified" value="verified" />
          <Tab
            label={
              processingTotal > 0
                ? `Processing (${processingTotal})`
                : 'Processing'
            }
            value="processing"
          />
          <Tab
            label={failedTotal > 0 ? `Failed (${failedTotal})` : 'Failed'}
            value="failed"
          />
          <Tab
            label={
              needsLinkingTotal > 0
                ? `Needs Linking (${needsLinkingTotal})`
                : 'Needs Linking'
            }
            value="needs_linking"
          />
          <Tab label="Stale" value="stale" />
          <Tab
            label={
              missingSourceTotal > 0
                ? `Missing Source (${missingSourceTotal})`
                : 'Missing Source'
            }
            value="missing_source"
          />
          <Tab label="By Vendor" value="by_vendor" />
          <Tab label="By Document Type" value="by_document_type" />
        </Tabs>
      </Box>
      {tab === 'by_vendor' ? (
        <VendorContractsTable
          onVendorClick={(vendorRow) => {
            setSelectedVendor(vendorRow)
            setTab('all')
          }}
        />
      ) : tab === 'by_document_type' ? (
        <DocumentTypeContractsTable
          onTypeClick={(docType) => {
            setSelectedDocumentType(docType)
            setTab('all')
          }}
        />
      ) : tab === 'stale' ? (
        <StaleContractsTable />
      ) : (
        <>
          {selectedVendor?.vendor_id && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label={`Filtered by vendor: ${selectedVendor.vendor_name}`}
                onDelete={() => setSelectedVendor(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
          {selectedDocumentType && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label={`Filtered by document type: ${selectedDocumentType}`}
                onDelete={() => setSelectedDocumentType(null)}
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
          <List
            actions={<ContractListActions tab={tab} />}
            filter={filter}
            empty={<ContractEmpty tab={tab} />}
            sort={{ field: 'created_at', order: 'DESC' }}
            sx={{
              '& .RaList-actions': {
                width: '100%',
                backgroundColor: 'transparent !important',
              },
              '& .RaTopToolbar-root': {
                width: '100%',
                backgroundColor: 'transparent !important',
              },
              '& .RaList-main': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            <PollingComponent tab={tab} />
            <Datagrid
              rowClick="edit"
              sx={{
                '& .MuiTableCell-head': {
                  fontWeight: 'bold',
                  backgroundColor: '#f9f8f7',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: '#f1f0ee !important',
                },
              }}
            >
              <TextField source="id" />
              <TextField
                source="filename"
                label="Document Name"
                sx={{ fontWeight: 'medium' }}
              />
              <ReferenceField
                source="vendor_id"
                reference="vendors"
                label="Linked Vendor"
                sortBy="vendor_name"
              >
                <TextField source="name" />
              </ReferenceField>
              <TextField source="contract_type" label="Type" />
              <FunctionField
                label="Status"
                sortBy="status"
                render={(record) => <ContractStatusChip record={record} />}
              />
              {tab === 'failed' && (
                <FunctionField
                  label="Reason"
                  sortable={false}
                  render={(record) => (
                    <Tooltip title={getReprocessingReason(record)}>
                      <span
                        style={{
                          display: 'inline-block',
                          maxWidth: 340,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'bottom',
                        }}
                      >
                        {getReprocessingReason(record)}
                      </span>
                    </Tooltip>
                  )}
                />
              )}
              <FunctionField
                label="AI Confidence"
                sortBy="ai_confidence"
                render={(record) => <ConfidenceIndicator record={record} />}
              />
              <DateField source="created_at" label="Uploaded" showTime />
              <FunctionField
                label="Actions"
                sortable={false}
                render={(record) => (
                  <div className="flex items-center space-x-1">
                    <RetryButton record={record} />
                    <EditButton />
                    <DeleteButton
                      mutationMode="pessimistic"
                      confirmTitle={`Delete Contract: ${record.filename}`}
                      confirmContent={`Are you sure you want to delete the contract "${record.filename}"? This action cannot be undone.`}
                    />
                  </div>
                )}
              />
            </Datagrid>
          </List>
        </>
      )}
      {/* 
          We place PollingComponent outside the List to ensure it remains mounted
          during tab changes and is not duplicated within the Datagrid or Empty state.
          However, PollingComponent needs access to ListContext for refetch(), 
          so we must wrap it in ListContextProvider if we put it outside.
          Alternatively, we can use useRefresh() inside PollingComponent if we don't
          need the surgical precision of ListContext's refetch().
          Given the user's "hammering" concern, using the surgical refetch() inside 
          List is safer, but we must only have ONE instance.
      */}
    </Box>
  )
}

export const ContractIcon = DocumentIcon
